import axios, {
  AxiosError,
  type AxiosInstance,
  type AxiosRequestConfig,
  type InternalAxiosRequestConfig,
} from 'axios';

import { env } from '@config/env';
import { STORAGE_KEYS } from '@constants/index';

export interface ApiError {
  status?: number;
  code?: string;
  message: string;
  details?: unknown;
  raw: unknown;
}

interface BackendErrorPayload {
  error?: { code?: string; message?: string; details?: unknown };
  detail?: string;
  message?: string;
  [key: string]: unknown;
}

interface RetryableConfig extends InternalAxiosRequestConfig {
  _retried?: boolean;
  _skipAuthRefresh?: boolean;
}

export const http: AxiosInstance = axios.create({
  baseURL: env.apiBaseUrl,
  timeout: env.apiTimeout,
  headers: { 'Content-Type': 'application/json' },
});

http.interceptors.request.use((config: InternalAxiosRequestConfig) => {
  const token = localStorage.getItem(STORAGE_KEYS.authToken);
  if (token && config.headers) {
    config.headers.Authorization = `Bearer ${token}`;
  }
  return config;
});

// ---------------------------------------------------------------------------
// Auth refresh hook
//
// AuthContext installs a refresher via `installAuthRefresh`. When a request
// comes back 401, we queue concurrent requests, run the refresher once, then
// retry. On refresh failure, the caller (AuthContext) is responsible for
// clearing local state and redirecting; the http client just passes the
// 401 through so callers can react.
// ---------------------------------------------------------------------------
type RefreshFn = () => Promise<string | null>;

let refreshFn: RefreshFn | null = null;
let pendingRefresh: Promise<string | null> | null = null;

export const installAuthRefresh = (fn: RefreshFn | null): void => {
  refreshFn = fn;
};

const runRefreshOnce = (): Promise<string | null> => {
  if (!refreshFn) return Promise.resolve(null);
  if (!pendingRefresh) {
    pendingRefresh = refreshFn().finally(() => {
      pendingRefresh = null;
    });
  }
  return pendingRefresh;
};

http.interceptors.response.use(
  (response) => response.data,
  async (error: AxiosError<BackendErrorPayload>) => {
    const config = error.config as RetryableConfig | undefined;
    const status = error.response?.status;

    // One-shot refresh on 401. Refresh + login endpoints opt out via
    // `_skipAuthRefresh` so we don't infinitely loop when refresh itself 401s.
    if (status === 401 && config && !config._retried && !config._skipAuthRefresh && refreshFn) {
      config._retried = true;
      const fresh = await runRefreshOnce();
      if (fresh) {
        config.headers = config.headers ?? {};
        (config.headers as Record<string, string>).Authorization = `Bearer ${fresh}`;
        return http.request(config);
      }
    }

    const payload = error.response?.data;
    const message =
      payload?.error?.message ??
      payload?.detail ??
      payload?.message ??
      error.message ??
      'Unexpected network error';
    const apiError: ApiError = {
      status,
      code: payload?.error?.code,
      message,
      details: payload?.error?.details ?? payload,
      raw: error,
    };
    return Promise.reject(apiError);
  },
);

// Helper to mark a single request as "do not refresh on 401". Used by the
// login/register/refresh calls themselves.
export const skipAuthRefresh = <T extends AxiosRequestConfig>(config: T): T =>
  ({ ...config, _skipAuthRefresh: true }) as T;
